import React, { Component, createRef } from "react";
import axios from "../../utils/axiosInstance";
import "./ChatDashboardHost.css";

class ChatDashboardHost extends Component {
  constructor(props) {
    super(props);

    this.state = {
      conversations: [],
      activeChatId: null,
      messages: [],
      customer_id: "",
      booking_id: "",
      inputText: "",
      isLoading: true,
      isSending: false,
      loadingMessages: false,
      conversationsError: "",
      messagesError: "",
      sendError: "",
      actionError: "",
      offerError: "",
      booking_status: "",
      loading: false,
      isSubmittingOffer: false,
      mobileView: "inbox",
      showDetailsModal: false,
      showUpdateStatusModal: false,
      showSpecialOfferModal: false,
      showGuestDetailsModal: false,
      offerPrice: "",
    };

    this.chatEndRef = createRef();
    this.scrollTimer = null;
  }

  componentDidMount() {
    const webUserId = localStorage.getItem("web_user_id");
    if (!webUserId) {
      window.location.href = "/";
      return;
    }
    this.getConversations();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.messages !== this.state.messages) this.scrollToBottom();
  }

  componentWillUnmount() {
    if (this.scrollTimer) window.clearTimeout(this.scrollTimer);
  }

  scrollToBottom = () => {
    if (this.scrollTimer) window.clearTimeout(this.scrollTimer);
    this.scrollTimer = window.setTimeout(() => {
      this.chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }, 50);
  };

  getErrorMessage = (error, fallback) =>
    error?.response?.data?.message || error?.response?.data?.error || error?.message || fallback;

  getConversations = async () => {
    const webUserId = localStorage.getItem("web_user_id");
    if (!webUserId) {
      this.setState({
        conversations: [],
        activeChatId: null,
        messages: [],
        isLoading: false,
        loadingMessages: false,
        conversationsError: "Your session could not be identified. Please sign in again.",
      });
      return;
    }

    this.setState({
      isLoading: true,
      conversationsError: "",
      messagesError: "",
      sendError: "",
      actionError: "",
    });

    try {
      const response = await axios.get(`/chat/host_conversations/${webUserId}`);
      const data = response?.data?.data;
      const conversations = Array.isArray(data) ? data : [];
      const firstChat = conversations[0] || null;

      this.setState(
        {
          conversations,
          activeChatId: firstChat?.id ?? null,
          customer_id: firstChat?.customer_id ?? "",
          booking_id: firstChat?.booking_id ?? "",
          booking_status: firstChat?.booking_status ?? "",
          messages: [],
          inputText: "",
          isLoading: false,
          loadingMessages: false,
          conversationsError: "",
          mobileView: "inbox",
        },
        () => {
          if (firstChat?.id != null) this.getMessages(firstChat.id);
        }
      );
    } catch (error) {
      this.setState({
        conversations: [],
        activeChatId: null,
        customer_id: "",
        booking_id: "",
        booking_status: "",
        messages: [],
        inputText: "",
        isLoading: false,
        loadingMessages: false,
        conversationsError: this.getErrorMessage(
          error,
          "We couldn't load your conversations. Check your connection and try again."
        ),
      });
    }
  };

  getMessages = async (chatId) => {
    const webUserId = localStorage.getItem("web_user_id");
    if (!webUserId || chatId == null) {
      this.setState({ messages: [], loadingMessages: false, messagesError: "" });
      return;
    }

    this.setState({ loadingMessages: true, messagesError: "", sendError: "", messages: [] });

    try {
      const response = await axios.get(`/chat/host_messages/${webUserId}/${chatId}`);
      const data = response?.data?.data;
      const messages = Array.isArray(data) ? data : [];

      // Ignore a response that belongs to a conversation the host has already left.
      if (String(this.state.activeChatId) !== String(chatId)) return;

      this.setState({ messages, loadingMessages: false, messagesError: "" });
    } catch (error) {
      if (String(this.state.activeChatId) !== String(chatId)) return;
      this.setState({
        messages: [],
        loadingMessages: false,
        messagesError: this.getErrorMessage(
          error,
          "We couldn't load these messages. Please try again."
        ),
      });
    }
  };

  sendMessage = async (event) => {
    if (event?.preventDefault) event.preventDefault();

    const { customer_id: customerId, inputText, messages, activeChatId, isSending } = this.state;
    const messageText = inputText.trim();
    const webUserId = localStorage.getItem("web_user_id");

    if (!messageText || isSending || !activeChatId || !customerId || !webUserId) return;

    const optimisticId = `pending-${Date.now()}`;
    const optimisticMessage = {
      id: optimisticId,
      sender: "partner",
      customer_id: customerId,
      message: messageText,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      pending: true,
    };

    this.setState({
      messages: [...messages, optimisticMessage],
      inputText: "",
      isSending: true,
      sendError: "",
    });

    try {
      const response = await axios.post("/chat/host_send", {
        host_id: webUserId,
        customer_id: customerId,
        message: messageText,
      });

      if (response?.data?.status && response.data.status !== "success") {
        throw new Error(response.data.message || "Your message couldn't be sent. Please try again.");
      }

      // Do not let a delayed request update a different conversation's UI.
      if (String(this.state.activeChatId) !== String(activeChatId)) return;
      this.setState((previousState) => ({
        messages: previousState.messages.map((message) =>
          message.id === optimisticId ? { ...message, pending: false } : message
        ),
        isSending: false,
      }));
    } catch (error) {
      if (String(this.state.activeChatId) !== String(activeChatId)) return;
      this.setState((previousState) => ({
        messages: previousState.messages.filter((message) => message.id !== optimisticId),
        inputText: previousState.inputText || messageText,
        isSending: false,
        sendError: this.getErrorMessage(error, "Your message couldn't be sent. Please try again."),
      }));
    }
  };

  handleKeyDown = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  };

  handleChatChange = (chatId) => {
    const selectedChat = this.state.conversations.find((chat) => String(chat.id) === String(chatId));
    if (!selectedChat) return;

    this.setState(
      {
        activeChatId: selectedChat.id,
        messages: [],
        inputText: "",
        isSending: false,
        customer_id: selectedChat.customer_id ?? "",
        booking_id: selectedChat.booking_id ?? "",
        booking_status: selectedChat.booking_status ?? "",
        messagesError: "",
        sendError: "",
        actionError: "",
        mobileView: "chat",
      },
      () => this.getMessages(selectedChat.id)
    );
  };

  handleMobileBack = () => this.setState({ mobileView: "inbox" });

  handleApproval = async () => {
    const { booking_id: bookingId, activeChatId, conversations } = this.state;
    if (!bookingId) {
      this.setState({ actionError: "Booking details are unavailable. Please refresh your inbox and try again." });
      return false;
    }

    this.setState({ loading: true, actionError: "" });
    try {
      const response = await axios.post("/chat/host_approval", { id: bookingId });
      if (response?.data?.status !== "success") {
        throw new Error(response?.data?.message || "The booking status couldn't be updated. Please try again.");
      }

      const updatedConversations = conversations.map((chat) =>
        String(chat.id) === String(activeChatId) ? { ...chat, booking_status: "Confirmed" } : chat
      );
      this.setState({
        booking_status: "Confirmed",
        conversations: updatedConversations,
        loading: false,
        actionError: "",
      });
      return true;
    } catch (error) {
      this.setState({
        loading: false,
        actionError: this.getErrorMessage(error, "The booking status couldn't be updated. Please try again."),
      });
      return false;
    }
  };

  handleSpecialOfferSubmit = async () => {
    const { booking_id: bookingId, offerPrice, activeChatId, conversations } = this.state;
    const numericPrice = Number(offerPrice);

    if (!bookingId) {
      this.setState({ offerError: "Booking details are unavailable. Please refresh your inbox and try again." });
      return;
    }
    if (!offerPrice || !Number.isFinite(numericPrice) || numericPrice <= 0) {
      this.setState({ offerError: "Enter a valid offer price greater than 0." });
      return;
    }

    this.setState({ isSubmittingOffer: true, offerError: "" });
    try {
      const response = await axios.post(
        "/special_offer_price",
        { booking_id: bookingId, total_amount: numericPrice },
        { headers: { "Content-Type": "application/json" } }
      );

      if (response?.data?.status !== "success") {
        throw new Error(response?.data?.message || "The special offer couldn't be sent. Please try again.");
      }

      const updatedTotal = response?.data?.booking?.total_amount ?? numericPrice;
      const updatedConversations = conversations.map((chat) =>
        String(chat.id) === String(activeChatId) ? { ...chat, total: updatedTotal } : chat
      );
      this.setState({
        conversations: updatedConversations,
        showSpecialOfferModal: false,
        offerPrice: "",
        offerError: "",
        isSubmittingOffer: false,
      });
    } catch (error) {
      this.setState({
        isSubmittingOffer: false,
        offerError: this.getErrorMessage(error, "The special offer couldn't be sent. Please try again."),
      });
    }
  };

  getBaseUrl = () =>
    axios.defaults.baseURL ? axios.defaults.baseURL.replace(/\/api\/?$/, "") : "";

  getImageUrl = (image) => {
    if (!image || typeof image !== "string") return "";
    if (/^https?:\/\//i.test(image)) return image;
    const baseUrl = this.getBaseUrl();
    if (image.startsWith("/")) return `${baseUrl}${image}`;
    if (image.startsWith("uploads/")) return `${baseUrl}/${image}`;
    if (image.startsWith("uploads/posts/")) return `${baseUrl}/${image}`;
    return `${baseUrl}/uploads/posts/${image}`;
  };

  getAvatarUrl = (avatar) => {
    if (!avatar || typeof avatar !== "string") return "";
    if (/^https?:\/\//i.test(avatar)) return avatar;
    if (avatar.startsWith("/")) return `${this.getBaseUrl()}${avatar}`;
    // Preserve server-provided relative avatar paths instead of treating them as post images.
    return avatar;
  };

  getInitials = (name) => {
    const parts = String(name || "?").trim().split(/\s+/).filter(Boolean);
    return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join("") || "?";
  };

  renderIcon = (type) => {
    const icons = {
      back: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>,
      send: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M22 2 11 13" /><path d="m22 2-7 20-4-9-9-4Z" /></svg>,
      link: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
          <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
        </svg>
      ),
      userOutline: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
      phone: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path d="M21.384 17.752a2.108 2.108 0 0 1-.522 2.712c-1.565 1.155-3.419 1.693-5.32 1.488-3.435-.365-6.6-2.14-8.86-5.006-2.18-2.76-3.32-6.136-3.218-9.52.029-1.92.74-3.722 2.016-5.12a2.094 2.094 0 0 1 2.85-.306l3.411 2.6c.725.553.978 1.543.593 2.348l-1.282 2.68c-.287.6-.201 1.309.22 1.838 1.053 1.326 2.38 2.403 3.868 3.141.597.296 1.312.22 1.848-.198l2.584-2.01c.783-.61 1.916-.543 2.617.155l3.197 3.198Z" />
        </svg>
      ),
      retry: <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M20 7v5h-5" /><path d="M4.8 9A7.5 7.5 0 0 1 18 6.5L20 12M4 17v-5h5" /><path d="M19.2 15A7.5 7.5 0 0 1 6 17.5L4 12" /></svg>,
    };
    return icons[type] || null;
  };

  renderConversationList = () => {
    const { conversations, activeChatId, isLoading, conversationsError } = this.state;

    if (isLoading) {
      return (
        <div className="chat-loading-list" role="status" aria-label="Loading conversations">
          {[1, 2, 3, 4].map((item) => (
            <div className="chat-skeleton" key={item}>
              <div className="skeleton-avatar" />
              <div className="skeleton-content"><span /><span /><span /></div>
            </div>
          ))}
        </div>
      );
    }

    if (conversationsError) {
      return (
        <div className="chat-empty chat-empty-error" role="alert">
          <div className="chat-empty-icon">!</div>
          <h3>Inbox unavailable</h3>
          <p>{conversationsError}</p>
          <button type="button" className="chat-retry-button" onClick={this.getConversations}>
            {this.renderIcon("retry")} Try again
          </button>
        </div>
      );
    }

    if (!conversations.length) {
      return (
        <div className="chat-empty">
          <div className="chat-empty-icon">💬</div>
          <h3>No conversations yet</h3>
          <p>Customer messages will appear here when someone contacts you about a booking.</p>
          <button type="button" className="chat-retry-button chat-retry-secondary" onClick={this.getConversations}>
            {this.renderIcon("retry")} Refresh inbox
          </button>
        </div>
      );
    }

    return conversations.map((chat) => {
      const avatarUrl = this.getAvatarUrl(chat.avatar);
      const statusClass = String(chat.booking_status || "pending").toLowerCase().replace(/\s+/g, "-");
      return (
        <button
          type="button"
          key={chat.id}
          className={`conversation-item ${String(activeChatId) === String(chat.id) ? "active" : ""}`}
          onClick={() => this.handleChatChange(chat.id)}
          aria-pressed={String(activeChatId) === String(chat.id)}
        >
          <div className="conversation-avatar">
            {avatarUrl ? (
              <img src={avatarUrl} alt={chat.customer_name || "Customer"} onError={(event) => { event.currentTarget.style.display = "none"; }} />
            ) : (
              <span className="avatar-initials">{this.getInitials(chat.customer_name)}</span>
            )}
            <span className={`online-status ${chat.status === "online" ? "online" : "offline"}`} />
          </div>
          <div className="conversation-content">
            <div className="conversation-top"><h3>{chat.customer_name || "Customer"}</h3></div>
            <p className="conversation-message">{chat.last_message || "No messages yet"}</p>
            <div className="conversation-bottom">
              <span className="conversation-booking">{chat.title || "Property booking"}</span>
              {chat.booking_status && <span className={`booking-status-pill ${statusClass}`}>{chat.booking_status}</span>}
            </div>
          </div>
        </button>
      );
    });
  };

  renderChatHeader = (activeChat) => {
    if (!activeChat?.id) {
      return (
        <div className="chat-screen-header chat-screen-header-empty">
          <div className="active-user-avatar active-user-avatar-placeholder"><span>💬</span></div>
          <div className="active-user-info"><h2>Messages</h2><span>Select a conversation to view messages</span></div>
        </div>
      );
    }

    const avatarUrl = this.getImageUrl(activeChat.avatar);
    return (
      <div className="chat-screen-header">
        <button type="button" className="mobile-back-button" onClick={this.handleMobileBack} aria-label="Back to inbox">
          {this.renderIcon("back")}
        </button>
        <div className="active-user-avatar">
          {avatarUrl ? (
            <img src={avatarUrl} alt={activeChat.customer_name || "Customer"} onError={(event) => { event.currentTarget.style.display = "none"; }} />
          ) : (
            <span className="avatar-initials">{this.getInitials(activeChat.customer_name)}</span>
          )}
          <span className="active-online-dot" />
        </div>
        <div className="active-user-info"><h2>{activeChat.customer_name || "Customer"}</h2></div>
        {activeChat.customer_phone && (
          <a href={`tel:${activeChat.customer_phone}`} className="chat-header-call" aria-label="Call Customer">
            {this.renderIcon("phone")}
          </a>
        )}
      </div>
    );
  };

  renderPropertySubHeader = (activeChat) => {
    if (!activeChat?.id) return null;
    const thumbnail = this.getImageUrl(activeChat.feature_image);

    return (
      <div className="mobile-chat-property-header">
        <div className="prop-header-top">
          {thumbnail ? (
            <img src={thumbnail} alt="Property" className="prop-thumb" onError={(event) => { event.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="prop-thumb prop-thumb-placeholder" aria-hidden="true">⌂</div>
          )}
          <div className="prop-title-wrap"><span className="prop-title">{activeChat.title || "Property details"}</span></div>
          <button type="button" className="prop-details-btn" onClick={() => this.setState({ showDetailsModal: true })}>Details</button>
        </div>
        <div className="prop-header-bottom">
          <span className={`status-text ${String(activeChat.booking_status || "pending").toLowerCase().replace(/\s+/g, "-")}`}>
            {activeChat.booking_status || "Pending"}
          </span>
          <span className="separator">•</span><span>{activeChat.date || "Dates not set"}</span>
          <span className="separator">•</span><span>{activeChat.adults ?? 0} Guests</span>
          <span className="separator">•</span><span>৳{activeChat.total ?? 0}</span>
        </div>
      </div>
    );
  };

  renderMessages = (activeChat) => {
    const { messages, loadingMessages, messagesError, isSending } = this.state;

    if (!activeChat?.id) {
      return (
        <div className="chat-empty chat-empty-main">
          <div className="chat-empty-icon">💬</div>
          <h3>Choose a conversation</h3>
          <p>Select a customer from your inbox to view the conversation.</p>
        </div>
      );
    }

    if (loadingMessages) {
      return <div className="messages-loading" role="status"><div className="message-loader received-loader" /><span>Loading messages…</span></div>;
    }

    if (messagesError) {
      return (
        <div className="chat-empty chat-empty-main chat-empty-error" role="alert">
          <div className="chat-empty-icon">!</div>
          <h3>Messages couldn't be loaded</h3>
          <p>{messagesError}</p>
          <button type="button" className="chat-retry-button" onClick={() => this.getMessages(activeChat.id)}>
            {this.renderIcon("retry")} Try again
          </button>
        </div>
      );
    }

    return (
      <>
        <div className="default-request-msg">
          <div className="default-msg-icon">{this.renderIcon("userOutline")}</div>
          <div>
            <strong>{activeChat.customer_name || "Customer"}</strong> has requested to book<br />
            <strong>“{activeChat.title || "Property booking"}”</strong>
            {activeChat.location && <><br /><span>{activeChat.location}</span></>}
          </div>
        </div>

        {messages.map((msg, index) => {
          const ownMessage = msg.sender === "partner" || msg.sender === "host";
          return (
            <div key={msg.id ?? `${msg.time || "message"}-${index}`} className={`message-row ${ownMessage ? "sent" : "received"}`}>
              <div className="message-bubble">
                <div className="message-text">{msg.message || ""}</div>
                <div className="message-meta">
                  <span>{msg.time || ""}</span>
                  {ownMessage && <span className="message-check">{msg.pending ? "Sending…" : "✓✓"}</span>}
                </div>
              </div>
            </div>
          );
        })}

        {messages.length === 0 && (
          <div className="thread-empty-state">
            <span className="thread-empty-icon">✉</span>
            <strong>No messages in this conversation yet</strong>
            <span>Send a message below to start the conversation.</span>
          </div>
        )}

        {activeChat.booking_status === "Confirmed" && activeChat.payment_status === "Paid" && (
          <div className="system-message-wrap">
            <div className="system-message-pill">
              {this.renderIcon("link")} Your booking has been confirmed. Use the call button above to contact the customer.
            </div>
          </div>
        )}

        {isSending && <div className="typing-indicator" aria-label="Sending message"><span /><span /><span /></div>}
        <div ref={this.chatEndRef} />
      </>
    );
  };

  renderChatInput = () => {
    const { inputText, isSending, sendError } = this.state;
    if (!this.state.activeChatId) return null;

    return (
      <div className="chat-input-area">
        {sendError && <div className="chat-inline-alert" role="alert"><span>{sendError}</span><button type="button" onClick={() => this.setState({ sendError: "" })} aria-label="Dismiss error">×</button></div>}
        <form className="chat-input-wrapper" onSubmit={this.sendMessage}>
          <button type="button" className="input-plus-button" aria-label="Attachments are not available yet" disabled title="Attachments are not available yet">+</button>
          <textarea
            className="chat-input"
            placeholder="Write a message… (Shift + Enter for a new line)"
            value={inputText}
            rows={1}
            onChange={(event) => this.setState({ inputText: event.target.value, sendError: "" })}
            onKeyDown={this.handleKeyDown}
            aria-label="Message"
          />
          <button type="submit" className="send-message-button" disabled={!inputText.trim() || isSending} aria-label="Send message">
            {this.renderIcon("send")}
          </button>
        </form>
      </div>
    );
  };

  renderModals = (activeChat) => {
    const {
      showDetailsModal,
      showUpdateStatusModal,
      showSpecialOfferModal,
      showGuestDetailsModal,
      offerPrice,
      loading,
      actionError,
      offerError,
      isSubmittingOffer,
    } = this.state;

    return (
      <>
        {showDetailsModal && (
          <div className="custom-modal-overlay" onClick={() => this.setState({ showDetailsModal: false })}>
            <div className="custom-modal-content" onClick={(event) => event.stopPropagation()}>
              <div className="custom-modal-header"><h3>Booking Details</h3><button type="button" onClick={() => this.setState({ showDetailsModal: false })} aria-label="Close">✕</button></div>
              <div className="custom-modal-body">
                <div className="details-card">
                  <p className="details-title">{activeChat.title || "Property booking"}</p>
                  <div className="details-section">
                    <h4>Trip Details</h4>
                    <div className="details-row"><span>Guests</span><strong>{activeChat.adults ?? 0} Adults</strong></div>
                    <div className="details-row"><span>Check in & out</span><strong>{activeChat.date || "Not set"}</strong></div>
                    <div className="details-row"><span>Status</span><strong>{activeChat.booking_status || "Pending"}</strong></div>
                    <div className="details-row"><span>Amount</span><strong>৳{activeChat.total ?? 0}</strong></div>
                  </div>
                  <button type="button" className="modal-btn-primary" onClick={() => this.setState({ showDetailsModal: false, showGuestDetailsModal: true })}>Guest Details</button>
                  <div className="details-section mt-15">
                    <h4>Contact Details</h4>
                    <p className="text-muted">Contact number and location will be provided once the booking is confirmed and paid.</p>
                  </div>
                  <button type="button" className="modal-btn-primary" onClick={() => this.setState({ showDetailsModal: false, showUpdateStatusModal: true, actionError: "" })} disabled={!activeChat.booking_id}>Update Status</button>
                  <button type="button" className="modal-btn-success mt-10" onClick={() => this.setState({ showDetailsModal: false, showSpecialOfferModal: true, offerError: "" })} disabled={!activeChat.booking_id}>Special Offer</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showGuestDetailsModal && (
          <div className="custom-modal-overlay" onClick={() => this.setState({ showGuestDetailsModal: false })}>
            <div className="custom-modal-content small-modal" onClick={(event) => event.stopPropagation()}>
              <div className="custom-modal-header"><h3>Guest Details</h3><button type="button" onClick={() => this.setState({ showGuestDetailsModal: false })} aria-label="Close">✕</button></div>
              <div className="custom-modal-body">
                <div className="guest-details-list">
                  <div className="details-row"><span>Name</span><strong>{activeChat.customer_name || "N/A"}</strong></div>
                  <div className="details-row"><span>Phone</span><strong>{activeChat.customer_phone || "N/A"}</strong></div>
                  <div className="details-row"><span>Email</span><strong className="break-email">{activeChat.customer_email || "N/A"}</strong></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {showUpdateStatusModal && (
          <div className="custom-modal-overlay" onClick={() => !loading && this.setState({ showUpdateStatusModal: false, actionError: "" })}>
            <div className="custom-modal-content small-modal" onClick={(event) => event.stopPropagation()}>
              <div className="custom-modal-header"><h3>Update Status</h3><button type="button" onClick={() => this.setState({ showUpdateStatusModal: false, actionError: "" })} disabled={loading} aria-label="Close">✕</button></div>
              <div className="custom-modal-body">
                <p>Confirm this booking request and update its status?</p>
                {actionError && <div className="modal-error-message" role="alert">{actionError}</div>}
                <div className="modal-actions">
                  <button type="button" className="modal-btn-secondary" onClick={() => this.setState({ showUpdateStatusModal: false, actionError: "" })} disabled={loading}>Cancel</button>
                  <button type="button" className="modal-btn-primary" onClick={async () => { const success = await this.handleApproval(); if (success) this.setState({ showUpdateStatusModal: false }); }} disabled={loading || !activeChat.booking_id}>
                    {loading ? "Updating…" : "Yes, Confirm"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSpecialOfferModal && (
          <div className="custom-modal-overlay" onClick={() => !isSubmittingOffer && this.setState({ showSpecialOfferModal: false, offerError: "" })}>
            <div className="custom-modal-content small-modal" onClick={(event) => event.stopPropagation()}>
              <div className="custom-modal-header"><h3>Special Offer</h3><button type="button" onClick={() => this.setState({ showSpecialOfferModal: false, offerError: "" })} disabled={isSubmittingOffer} aria-label="Close">✕</button></div>
              <div className="custom-modal-body">
                <div className="offer-summary-card">
                  <p><strong>Property:</strong> {activeChat.title || "N/A"}</p>
                  <p><strong>Guests:</strong> {activeChat.adults ?? 0}</p>
                  <p><strong>Current total:</strong> ৳{activeChat.total ?? 0}</p>
                </div>
                <label className="modal-label" htmlFor="host-special-offer-price">Enter new offer price (BDT)</label>
                <input id="host-special-offer-price" type="number" min="1" step="1" className="modal-input" placeholder="Enter special total price" value={offerPrice} onChange={(event) => this.setState({ offerPrice: event.target.value, offerError: "" })} />
                {offerError && <div className="modal-error-message" role="alert">{offerError}</div>}
                <button type="button" className="modal-btn-success mt-15" onClick={this.handleSpecialOfferSubmit} disabled={!offerPrice || isSubmittingOffer}>
                  {isSubmittingOffer ? "Sending offer…" : "Send Offer"}
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  render() {
    const { conversations, activeChatId, mobileView, isLoading } = this.state;
    const activeChat = conversations.find((chat) => String(chat.id) === String(activeChatId)) || {};

    return (
      <div className="chat-dashboard-host">
        <div className="chat-desktop-layout">
          <aside className="inbox-sidebar">
            <div className="inbox-header">
              <div><span className="page-eyebrow">MESSAGES</span><h1>Inbox</h1></div>
              {!isLoading && conversations.length > 0 && <div className="inbox-count">{conversations.length}</div>}
            </div>
            <div className="conversation-list">{this.renderConversationList()}</div>
          </aside>

          <main className="chat-main-panel">
            {this.renderChatHeader(activeChat)}
            <div className="messages-area">
              {this.renderPropertySubHeader(activeChat)}
              <div className="messages-thread">{this.renderMessages(activeChat)}</div>
            </div>
            {this.renderChatInput()}
          </main>
        </div>

        <div className="chat-mobile-layout">
          {mobileView === "inbox" || !activeChatId ? (
            <div className="mobile-inbox-screen">
              <div className="mobile-inbox-header">
                <div><span className="page-eyebrow">MESSAGES</span><h1>Inbox</h1></div>
                {!isLoading && conversations.length > 0 && <div className="mobile-inbox-count">{conversations.length}</div>}
              </div>
              <div className="mobile-conversation-list">{this.renderConversationList()}</div>
            </div>
          ) : (
            <div className="mobile-chat-screen">
              {this.renderChatHeader(activeChat)}
              {this.renderPropertySubHeader(activeChat)}
              <div className="mobile-messages-area">{this.renderMessages(activeChat)}</div>
              {this.renderChatInput()}
            </div>
          )}
        </div>

        {this.renderModals(activeChat)}
      </div>
    );
  }
}

export default ChatDashboardHost;
